'use client';

import { useCallback, useEffect, useState } from 'react';

import { HeadingText } from '@/components/atoms/HeadingText';
import { Button } from '@/components/atoms/Button';
import { SpaceWow } from '@/components/SpaceWow';
import Link from 'next/link';
import { InlineComponents } from './InlineComponentsList';
import { IconAnalogClock } from '@/components/icons';

export interface RecentPostsControllerProps {
	testId?: string;
	categoryName: string;
}

type CatArticlesProps = {
	title: string;
	slug: string;
	createdAt: string;
	read_time: number;
};

const RecentPostsController = (props: RecentPostsControllerProps) => {
	const { testId = 'RecentPostsController', categoryName } = props;

	const [catArticles, setCatArticles] = useState<CatArticlesProps[]>([]);
	const [isLoading, setIsLoading] = useState(false); // Just an indicator for the user that we are loading
	const [isLastPage, setIsLastPage] = useState(false);
	const [lastCount, setLastCount] = useState(0);
	const [notEnoughArticles, setNotEnoughArticles] = useState(false);
	const [page, setPage] = useState(1);

	const currentPosts = useCallback(async () => {
		try {
			const response = await fetch(
				`/api/strapi/categories/articles/?category=${categoryName
					.toLowerCase()
					.replace(' ', '-')}&page=${page}`,
				{
					cache: 'no-cache',
				},
			);

			const articles = await response.json();

			// We can get the page count from strapi to disable the button if there is no more articles
			if (page === articles.meta.pagination.pageCount) {
				setIsLastPage(true);
			}

			if (
				page === articles.meta.pagination.pageCount &&
				articles.data.length < 20
			) {
				const remainder = articles.data.length % 8;
				setLastCount(remainder);
				setNotEnoughArticles(true);
			}

			// We need to double check if the articles are already in the state
			// I think this only happens in dev because of strict mode, but I want to be sure
			setCatArticles((prevArticles) => {
				const currentIds = new Set(prevArticles.map((a) => a.title));
				const newArticles = articles.data.filter(
					(a: { title: string }) => !currentIds.has(a.title),
				);
				setIsLoading(false);
				return [...prevArticles, ...newArticles];
			});
		} catch (error) {
			setIsLoading(false);
			console.error('Failed to fetch articles:', error);
		}
	}, [categoryName, page]);

	useEffect(() => {
		currentPosts();
	}, [currentPosts]);

	// This funtion will set the laoding state and update the page we are to load more articles
	const handleLoadMore = () => {
		setIsLoading(true);
		setPage((prevPage) => prevPage + 1);
	};

	return (
		<div data-testid={testId}>
			<HeadingText
				tag="h3"
				className="font-semibold tracking-tight lg:text-left"
			>
				Recent
			</HeadingText>

			<SpaceWow spacer_06 lg__spacer_10 />

			<div className="grid gap-10">
				{catArticles.length > 0 &&
					catArticles.map((article, index) => {
						const postedOn = new Date(article.createdAt);

						return (
							<div key={article.title}>
								<article>
									<Link href={`/expert-advice/${article.slug}`}>
										<HeadingText
											tag="h4"
											className="font-medium tracking-tight text-sky-700 hover:text-sky-800 lg:text-left"
										>
											{article.title}
										</HeadingText>
									</Link>
									<div className="flex items-center">
										<span className="text-sm text-slate-500 lg:text-base">
											{postedOn.toLocaleDateString('en-US', {
												month: 'long',
												day: 'numeric',
												year: 'numeric',
											})}
										</span>
										{article.read_time ? (
											<span className="ml-3 flex items-center justify-center text-sm text-slate-500 lg:ml-6 lg:text-base">
												<IconAnalogClock className="h-5 w-5 fill-slate-500 lg:w-6" />{' '}
												{article.read_time} min read
											</span>
										) : null}
									</div>
								</article>
								{(index + 1) % 8 === 0 &&
									!(
										isLastPage &&
										!notEnoughArticles &&
										index + 8 - catArticles.length < lastCount
									) && (
										<div className="mt-10">
											{
												InlineComponents[
													(Math.floor((index + 1) / 8) - 1) %
														InlineComponents.length
												]?.component
											}
										</div>
									)}
							</div>
						);
					})}
			</div>

			<SpaceWow spacer_10 lg__spacer_14 />

			{!isLastPage && (
				<div className="mb-10 lg:mb-0 lg:flex">
					<Button
						onClick={handleLoadMore}
						variant="large-outline"
						className="w-full py-4 md:w-auto lg:py-5"
						disabled={isLoading}
					>
						{isLoading ? 'Loading...' : 'Show more'}
					</Button>
				</div>
			)}
		</div>
	);
};

export default RecentPostsController;
